import { Component, inject, Injector, OnDestroy } from "@angular/core";
import { MatIconRegistry } from "@angular/material/icon";
import { RouterOutlet } from "@angular/router";
import { NgxLoadingBar } from "@ngx-loading-bar/core";
import { TranslateService } from "@ngx-translate/core";
import { DEFAULT_LANGUAGE, LANGUAGE_CODES } from "./core/constants/constants";
import { CURRENT_LANGUAGE_KEY } from "./core/constants/storage-keys";
import { OfflineMessageComponent } from "./shared/components/offline-message/offline-message.component";

@Component({
	selector: "app-root",
	imports: [RouterOutlet, NgxLoadingBar, OfflineMessageComponent],
	templateUrl: "./app.component.html",
})
export class AppComponent implements OnDestroy {
	/**
	 * This injector is used by `@Confirm` decorator to access ConfirmService
	 */
	static injector: Injector | null;

	registry = inject(MatIconRegistry);
	translate = inject(TranslateService);

	constructor(injector: Injector) {
		AppComponent.injector = injector;

		// if (localStorage.getItem("access-token")) {
		// 	localStorage.clear();
		// }

		const language = localStorage.getItem(CURRENT_LANGUAGE_KEY) || DEFAULT_LANGUAGE;
		this.translate.addLangs(LANGUAGE_CODES);
		this.translate.use(language);

		// Use new material symbols icons.
		// https://material.angular.dev/components/icon/overview#font-icons-with-ligatures
		// https://developers.google.com/fonts/docs/material_symbols,
		this.registry.setDefaultFontSetClass("material-symbols-outlined");

		this.guardNumberInputs();
	}

	ngOnDestroy(): void {
		document.removeEventListener("wheel", this.preventNumberInputScroll, { capture: true });
		document.removeEventListener("keydown", this.preventNumberInputArrowKeys, { capture: true });
	}

	/**
	 * Globally prevents `<input type="number">` values from being silently mutated
	 * by mouse-wheel scroll or arrow keys while focused (Zeraki Flow #6508).
	 *
	 * Element-level listeners are added in capture phase with `passive: false` so
	 * `preventDefault()` actually blocks the value change at the document level.
	 * This covers every number input in the app, including ones whose component does
	 * not import PreventNumberModificationWithUpDownKeysDirective.
	 */
	private guardNumberInputs(): void {
		document.addEventListener("wheel", this.preventNumberInputScroll, { passive: false, capture: true });
		document.addEventListener("keydown", this.preventNumberInputArrowKeys, { capture: true });
	}

	private preventNumberInputScroll = (event: WheelEvent): void => {
		if (this.isFocusedNumberInput(event.target)) {
			event.preventDefault();
		}
	};

	private preventNumberInputArrowKeys = (event: KeyboardEvent): void => {
		if ((event.key === "ArrowUp" || event.key === "ArrowDown") && this.isFocusedNumberInput(event.target)) {
			event.preventDefault();
		}
	};

	private isFocusedNumberInput(target: EventTarget | null): boolean {
		return target instanceof HTMLInputElement && target.type === "number" && target === document.activeElement;
	}
}
